import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { BookOpen, Copy, Plus, Star } from 'lucide-react';
import { STORE_LAYOUT_LABELS } from '@bp/shared';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import type { TemplateSummary } from '@/lib/types';
import { fmtDateTime } from '@/lib/utils';
import { Badge, Button, Card, Dialog, Empty, ErrorNote, Field, Input, Loading, PageHeader, type Tone } from '@/components/ui';

const statusTone: Record<string, Tone> = { draft: 'neutral', active: 'pass', inactive: 'warn', archived: 'neutral' };

export function visibilityText(t: Pick<TemplateSummary, 'visibility' | 'layouts' | 'store_ids'>) {
  if (t.visibility === 'all_stores') return 'All stores';
  if (t.visibility === 'layouts') return t.layouts.length ? t.layouts.map((l) => STORE_LAYOUT_LABELS[l]).join(', ') : 'No layouts picked';
  return `${t.store_ids.length} specific store${t.store_ids.length === 1 ? '' : 's'}`;
}

export function TemplatesPage() {
  const { can } = useAuth();
  const qc = useQueryClient();
  const nav = useNavigate();
  const q = useQuery({ queryKey: ['templates'], queryFn: () => api.get<TemplateSummary[]>('/api/templates') });
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const manage = can('manage_templates');

  async function create() {
    setBusy(true);
    try {
      const t = await api.post<{ id: string }>('/api/templates', { name });
      await qc.invalidateQueries({ queryKey: ['templates'] });
      nav(`/templates/${t.id}`);
    } catch (e) {
      toast.error((e as Error).message);
      setBusy(false);
    }
  }
  async function duplicate(id: string) {
    try {
      const t = await api.post<{ id: string }>(`/api/templates/${id}/duplicate`);
      await qc.invalidateQueries({ queryKey: ['templates'] });
      nav(`/templates/${t.id}`);
    } catch (e) { toast.error((e as Error).message); }
  }

  return (
    <>
      <PageHeader
        title="Audit templates"
        subtitle="Edits are drafts until published. Each publish creates a new version; audits keep the version they started on."
        actions={manage && <Button onClick={() => setCreating(true)}><Plus className="size-4" />New template</Button>}
      />
      {q.isLoading ? <Loading /> : q.error ? <ErrorNote error={q.error} /> : q.data!.length === 0 ? (
        <Card><Empty icon={<BookOpen className="size-8" />} title="No templates yet." /></Card>
      ) : (
        <Card className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-muted-fg uppercase"><tr className="border-b"><th className="px-4 py-2 font-medium">Template</th><th className="px-4 py-2 font-medium">Status</th><th className="px-4 py-2 font-medium">Version</th><th className="px-4 py-2 font-medium">Items</th><th className="px-4 py-2 font-medium">Used at</th><th className="px-4 py-2 font-medium">Audits</th><th /></tr></thead>
            <tbody>
              {q.data!.map((t) => (
                <tr key={t.id} className="border-b last:border-0 hover:bg-muted/50">
                  <td className="px-4 py-2.5">
                    <Link to={`/templates/${t.id}`} className="inline-flex items-center gap-1.5 font-medium hover:underline">{t.is_default && <Star className="size-3.5 fill-warn text-warn" />}{t.name}</Link>
                    <p className="text-xs text-muted-fg">{[t.audit_type, t.operational_purpose].filter(Boolean).join(' · ')}</p>
                  </td>
                  <td className="px-4 py-2.5"><Badge tone={statusTone[t.status]} className="capitalize">{t.status}</Badge></td>
                  <td className="px-4 py-2.5 whitespace-nowrap">{t.current_version_no ? `v${t.current_version_no}` : '—'}{t.has_unpublished_changes && <Badge tone="warn" className="ml-2">Unpublished edits</Badge>}</td>
                  <td className="tabular px-4 py-2.5">{t.item_count}</td>
                  <td className="px-4 py-2.5">{visibilityText(t)}</td>
                  <td className="tabular px-4 py-2.5">{t.audit_count}</td>
                  <td className="px-4 py-2.5 text-right">
                    {manage && <Button size="sm" variant="ghost" onClick={() => duplicate(t.id)} title="Duplicate"><Copy className="size-4" /></Button>}
                    <span className="text-xs text-muted-fg">{fmtDateTime(t.updated_at)}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
      <Dialog open={creating} onOpenChange={setCreating} title="New template" footer={<><Button variant="outline" onClick={() => setCreating(false)}>Cancel</Button><Button onClick={create} loading={busy} disabled={!name.trim()}>Create</Button></>}>
        <Field label="Name"><Input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Closing checklist" /></Field>
      </Dialog>
    </>
  );
}
